/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * Central legal metadata for the SanitàNexus platform.
 *
 * Single source of truth: any change here is automatically propagated to
 * the /legal/* documents, the global footer, PDF invoices, and the
 * FatturaPA XML generator. Do NOT hardcode these values anywhere else.
 *
 * These values reflect a *software-only* individual licensor operating
 * under the Italian Intellectual Property Law (n. 633/1941), NOT a
 * clinical operator: the licensor never touches PHI / patient data. Each
 * buyer clinic acts as the sole Data Controller for the healthcare
 * information processed by their own instance.
 */
export const LEGAL_OWNER = Object.freeze({
  // Individual licensor
  fullName: 'Marco Panzetti',
  fiscalCode: 'PNZMRC89M14F251C',

  // Fiscal domicile / legal address
  address: {
    street: 'Via Gioacchino Rossini 58',
    zip: '27010',
    city: 'Ceranova',
    province: 'PV',
    country: 'Italy',
  },
  addressLine: 'Via Gioacchino Rossini 58, 27010 Ceranova (PV), Italy',

  // VAT / P.IVA — exempt (individual under Italian IP law)
  vat: null,
  vatExemptionNotice:
    'Software licensing provided by an individual under the Italian Intellectual Property Law n. 633/1941, exempt from VAT requirements per Art. 3, c. 4, lett. a, DPR 633/1972.',

  // Data Protection Officer — not applicable
  dpo: null,
  dpoRationale:
    'Il Titolare fornisce esclusivamente il codice sorgente del software e non ha alcun accesso ai dati sanitari degli utenti finali (PHI). L\'architettura è strettamente local-first e viene ospitata direttamente sull\'infrastruttura del cliente (clinica) che agisce quale unico Titolare del Trattamento.',

  // Contact
  contactEmail: 'marcopanzetti@gmail.com',

  // Governing law
  jurisdiction: 'Foro competente: Pavia (PV), Italia',

  // Version tracking
  lastReview: '2026-08-08',
});

/**
 * Add-on module exclusion list — used by the Terms of Service and the
 * pricing pages. The Core license explicitly does NOT include these.
 */
export const ADDON_MODULES_EXCLUDED = Object.freeze([
  {
    id: 'iot_telemonitoring',
    name: 'IoT Telemonitoring',
    description: 'Real-time device streaming (BP, SpO2, ECG), edge inference, alert routing.',
  },
  {
    id: 'allergy_diary',
    name: 'Allergy Diary',
    description: 'Cross-referenced allergen tracking with local drug interaction alerts.',
  },
  {
    id: 'graphometric_signature',
    name: 'Graphometric Signature',
    description: 'FEA-compliant biometric signature capture with tablet integration.',
  },
  {
    id: 'predictive_ai',
    name: 'Predictive AI',
    description: 'ML-based risk stratification, readmission prediction, and diagnostic assist.',
  },
]);

// Small helpers so components can concatenate a compact one-liner without
// repeating template strings.
export const LEGAL_OWNER_ONELINER = () =>
  `${LEGAL_OWNER.fullName} · CF ${LEGAL_OWNER.fiscalCode} · ${LEGAL_OWNER.addressLine}`;

export const LEGAL_OWNER_MULTILINE = () => [
  LEGAL_OWNER.fullName,
  `Codice Fiscale: ${LEGAL_OWNER.fiscalCode}`,
  LEGAL_OWNER.addressLine,
  LEGAL_OWNER.vatExemptionNotice,
  `Contatti: ${LEGAL_OWNER.contactEmail}`,
];

export default LEGAL_OWNER;
